import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { axiosInstance } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);

    useEffect(() => {
        if (token) {
            // Set token on both axios defaults and axiosInstance
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            fetchUser();
        } else {
            setLoading(false);
        }
    }, [token]);

    const fetchUser = async () => {
        try {
            const response = await axiosInstance.get(`/auth/me`);
            setUser(response.data);
            
            // Check if admin
            try {
                const adminRes = await axiosInstance.get(`/auth/is-admin`);
                setIsAdmin(adminRes.data.is_admin);
            } catch {
                setIsAdmin(false);
            }
        } catch (error) {
            console.error('Failed to fetch user:', error);
            logout();
        } finally {
            setLoading(false);
        }
    };

    const login = async (email, password) => {
        const response = await axiosInstance.post(`/auth/login`, { email, password });
        const { access_token, user: userData } = response.data;
        localStorage.setItem('token', access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        setToken(access_token);
        setUser(userData);
        
        // Check if admin
        try {
            const adminRes = await axiosInstance.get(`/auth/is-admin`);
            setIsAdmin(adminRes.data.is_admin);
        } catch {
            setIsAdmin(false);
        }
        
        return userData;
    };

    const register = async (email, password, name) => {
        const response = await axiosInstance.post(`/auth/register`, { email, password, name });
        const { access_token, user: userData } = response.data;
        localStorage.setItem('token', access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        setToken(access_token);
        setUser(userData);
        return userData;
    };

    const logout = () => {
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
        delete axiosInstance.defaults.headers.common['Authorization'];
        setToken(null);
        setUser(null);
        setIsAdmin(false);
    };

    const refreshUser = async () => {
        if (token) {
            await fetchUser();
        }
    };

    // Premium access: either premium subscription OR admin
    const isPremium = user?.subscription_tier === 'premium' || isAdmin;

    return (
        <AuthContext.Provider value={{ user, token, loading, login, register, logout, refreshUser, isPremium, isAdmin }}>
            {children}
        </AuthContext.Provider>
    );
};
